import { useQuery } from '@tanstack/react-query';
import { formatIstDate, formatMoney, inboxApi } from '@wa/shared';

/**
 * Which plan the team is on, and how much of it is in use.
 *
 * Read-only on purpose. Changing the plan is a conversation with PSM, not a
 * button; what this screen owes the team is the numbers that explain a refusal
 * elsewhere — "all 5 agents are in use" — and the date sending would stop.
 */
export function PlanPanel() {
  const mine = useQuery({ queryKey: ['my-plan'], queryFn: inboxApi.myPlan });

  if (!mine.data) return null;
  const { plan, usage } = mine.data;

  if (!plan) {
    return (
      <section className="rounded-(--radius-card) border border-(--color-line) p-4">
        <h2 className="text-body font-semibold">Your plan</h2>
        <p className="mt-1 max-w-prose text-small text-(--color-muted)">
          This account is not on a plan yet. Nothing is limited — {usage.agents} agents and {usage.numbers} number{usage.numbers === 1 ? '' : 's'} in use.
          Contact PSM Softtech to put the account on one.
        </p>
      </section>
    );
  }

  const tone = plan.daysLeft < 0 ? 'text-(--color-bad)' : plan.daysLeft <= 30 ? 'text-(--color-warn)' : 'text-(--color-muted)';
  const left = plan.daysLeft < 0
    ? `ended on ${formatIstDate(plan.endsOn)}`
    : plan.daysLeft === 0 ? 'ends today'
    : `renews ${formatIstDate(plan.endsOn)} · ${plan.daysLeft} days left`;

  return (
    <div className="flex flex-col gap-4">
      <section className="rounded-(--radius-card) border border-(--color-line) p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-body font-semibold">{plan.name} plan</h2>
          <span className={`text-small ${tone}`}>{left}</span>
        </div>

        <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-small">
          <dt className="text-(--color-muted)">Agents</dt>
          <dd><Meter used={usage.agents} allowed={plan.agentsAllowed} /></dd>
          <dt className="text-(--color-muted)">WhatsApp numbers</dt>
          <dd><Meter used={usage.numbers} allowed={plan.numbersAllowed} /></dd>
          <dt className="text-(--color-muted)">Mobile app</dt>
          <dd>
            {plan.mobileSeats === 0
              ? 'not on this plan'
              : <Meter used={usage.mobileUsers} allowed={plan.mobileSeats} />}
          </dd>
          <dt className="text-(--color-muted)">API &amp; webhooks</dt>
          <dd>{plan.apiAccess ? `included · ${usage.apiKeys} key${usage.apiKeys === 1 ? '' : 's'} issued` : 'not in this plan'}</dd>
          <dt className="text-(--color-muted)">Chatbots</dt>
          <dd>
            {plan.botsLevel === 0 ? 'not in this plan'
              : plan.botsLevel === 1 ? 'keyword and menu bots'
              : 'any bot, including ones that call your own systems'}
            {' · '}{usage.liveBots} live
          </dd>
        </dl>

        {plan.daysLeft <= 30 && (
          <p className={`mt-3 text-small ${tone}`}>
            {plan.daysLeft < 0
              ? 'The plan has ended. Sending stops seven days after the end date unless it is renewed.'
              : 'Renew before the end date to keep sending without interruption.'}
            {' '}Contact PSM Softtech to renew.
          </p>
        )}
      </section>

      <p className="max-w-prose text-small text-(--color-muted)">
        Need more? An extra agent is ₹{formatMoney(plan.extraAgentMonthly)} a month, an extra number
        ₹{formatMoney(plan.extraNumberMonthly)} a month, and the mobile app is ₹{formatMoney(plan.mobilePackMonthly)} a month
        for up to {plan.mobilePackSize} users (you choose who, under Team) — or move to a bigger plan. Either way, contact
        PSM Softtech and it is on your account the same day.
      </p>
    </div>
  );
}

function Meter({ used, allowed }: { used: number; allowed: number }) {
  const full = used >= allowed;
  return (
    <span className={full ? 'font-medium' : ''}>
      {used} of {allowed}
      {full && <span className="ml-1.5 text-(--color-muted)">— all in use</span>}
    </span>
  );
}
